import PropTypes from 'prop-types';
import React from 'react';
import { map as _map } from 'lodash';
import classNames from 'classnames/bind';

import { locale } from 'utils/locale';
import { hasFinancingInLocale } from 'utils/localeServices';
import { src } from 'utils/imgix';

import { Icon } from 'sheets';
import localize from 'components/Localize/Localize';
import Modal from 'components/Modal/Modal';

import styles from './FinancingModal.css';
import translations from './translations';

const cx = classNames.bind(styles);

class FinancingModal extends React.Component {
  renderFinancerLogo() {
    if (this.props.t.financer.iconKey) {
      return (
        <Icon
          className={styles[`icon-klarna`]}
          name={this.props.t.financer.iconKey}
        />
      );
    }

    return (
      <img
        alt={this.props.t.financer.name}
        src={src(this.props.t.financer.logoPath, 90)}
      />
    );
  }

  renderLink() {
    if (!this.props.t.link || !this.props.t.linkPath) {
      return null;
    }

    return (
      <a
        className={styles[`link`]}
        href={this.props.t.linkPath}
      >
        {this.props.t.link}
      </a>
    );
  }

  renderColumns() {
    return (
      <div className={styles[`columns`]}>
        {_map(this.props.t.columns, (column) => {
          const columnIcon = column.icon ? (
            <Icon
              className={styles[`column-icon`]}
              name={column.icon}
            />
          ) : null;
          const columnClassName = cx(`column`, {
            [`column--${this.props.t.columns.length}`]: true,
            'column--iconless': !columnIcon,
          });

          return (
            <div
              className={columnClassName}
              key={column.title}
            >
              {columnIcon}
              <div className={styles[`column-title`]}>
                {column.title}
              </div>
              <p>
                {column.copy}
              </p>
            </div>
          );
        })}
      </div>
    );
  }

  renderSecondary() {
    const secondaryTitle = this.props.t.subtitle ? <h2 className={styles[`secondary-heading`]}>{this.props.t.subtitle}</h2> : null;
    const secondaryCopy = this.props.t.subcopy ? <p className={styles[`copy`]}>{this.props.t.subcopy}</p> : null;
    const list = _map(this.props.t.list, (item) => {
      return (
        <li
          className={styles[`list-item`]}
          key={item}
        >
          <Icon
            className={styles[`checkmark`]}
            name="checkmark-solo"
          />
          {item}
        </li>
      );
    });

    return (
      <div className={styles[`secondary`]}>
        {secondaryTitle}
        {secondaryCopy}
        <ul className={styles[`list`]}>
          {list}
        </ul>
      </div>
    );
  }

  renderParagraphLink(linkPath, link) {
    return (
      <a
        className={styles[`link`]}
        href={linkPath}
      >
        {link}
      </a>
    );
  }

  renderFooterParagraphs() {
    return _map(this.props.t.footer.paragraphs, (paragraph, key) => {
      return (
        <p key={key}>
          {paragraph.heading && <strong>{paragraph.heading}:&nbsp;</strong>}
          {paragraph.content && <span>{paragraph.content}&nbsp;</span>}
          {paragraph.link && this.renderParagraphLink(paragraph.linkPath, paragraph.link)}
          {paragraph.footer && <span>{paragraph.footer}</span>}
        </p>
      );
    });
  }

  render() {
    if (locale(this.props.locale).isDE() || !hasFinancingInLocale(this.props.locale)) {
      return null;
    }

    return (
      <Modal keyName={this.props.t.modalKey}>
        <div>
          <div className={styles[`container`]}>
            <h1>
              {this.renderFinancerLogo()}
              <span className={styles[`title-plus`]}>
                +
              </span>
              <Icon
                className={styles[`icon-casper`]}
                name="logo"
              />
            </h1>
            <h2 className={styles[`heading`]}>
              {this.props.t.title}
            </h2>
            <p className={styles[`copy`]}>
              {this.props.t.copy}&nbsp;
              {this.renderLink()}
            </p>
            {this.renderColumns()}
            {this.renderSecondary()}
          </div>
          <footer className={styles[`footer`]}>
            <p>
              <strong>
                {this.props.t.footer.title}
              </strong>
            </p>
            {this.renderFooterParagraphs()}
          </footer>
        </div>
      </Modal>
    );
  }
}

FinancingModal.propTypes = {
  t: PropTypes.shape({
    columns: PropTypes.array.isRequired,
    copy: PropTypes.string.isRequired,
    financer: PropTypes.shape({
      iconKey: PropTypes.string,
      logoPath: PropTypes.string,
      name: PropTypes.string.isRequired,
    }).isRequired,
    footer: PropTypes.shape({
      title: PropTypes.string.isRequired,
      paragraphs: PropTypes.arrayOf(PropTypes.shape({
        content: PropTypes.string,
        footer: PropTypes.string,
        heading: PropTypes.string,
        link: PropTypes.string,
        linkPath: PropTypes.string,
      })).isRequired,
    }).isRequired,
    link: PropTypes.string,
    linkPath: PropTypes.string,
    list: PropTypes.array.isRequired,
    modalKey: PropTypes.string.isRequired,
    subcopy: PropTypes.string,
    subtitle: PropTypes.string,
    title: PropTypes.string.isRequired,
  }),
};

export default localize(FinancingModal, translations, false);
